import { useMutation } from '@tanstack/react-query';
import { ContactEmailUpdate, EnrollmentInfo, NavInfoUpdate, NavObjectUpdate, NavTypeUpdate, OnboardingUpdate, DisplayNameUpdate, FinalUpdate, TextPrefs, NavInfo, NavType } from '@usacsmideo/types';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../constants/firebaseModular';
import { APPLICATION_STATUS, createLastUpdated, UPDATE_SOURCE } from '../utilities/updateHelpers';

const updateUserDoc = async (
  payload:
    | ContactEmailUpdate
    | EnrollmentInfo
    | NavTypeUpdate
    | NavInfoUpdate
    | NavObjectUpdate
    | OnboardingUpdate
    | DisplayNameUpdate
    | FinalUpdate
    | TextPrefs,
) => {
  let data;
  const docRef = doc(db, 'users', payload.patientId);
  const lastUpdated = createLastUpdated(UPDATE_SOURCE.PORTAL, payload.patientId);

  // HANDLE ContactEmailUpdate UPDATES
  function isContactEmailUpdate(
    payload:
      | ContactEmailUpdate
      | EnrollmentInfo
      | NavTypeUpdate
      | NavInfoUpdate
      | NavObjectUpdate
      | OnboardingUpdate
      | DisplayNameUpdate
      | FinalUpdate
      | TextPrefs,
  ): payload is ContactEmailUpdate {
    return (payload as ContactEmailUpdate).contactEmail !== undefined;
  }
  if (isContactEmailUpdate(payload)) {
    data = {
      contactEmail: payload.contactEmail,
      lastUpdated,
      applicationStatus: APPLICATION_STATUS.OPEN,
    };
    await setDoc(docRef, data, { merge: true });
  }

  // HANDLE EnrollmentInfo UPDATES
  function isEnrollmentInfo(
    payload:
      | ContactEmailUpdate
      | EnrollmentInfo
      | NavTypeUpdate
      | NavInfoUpdate
      | NavObjectUpdate
      | OnboardingUpdate
      | DisplayNameUpdate
      | FinalUpdate
      | TextPrefs,
  ): payload is EnrollmentInfo {
    return (payload as EnrollmentInfo).campaignId !== undefined;
  }
  if (isEnrollmentInfo(payload) && payload.campaignId) {
    data = {
      enrollmentInfo: {
        campaignId: payload.campaignId,
        patientId: payload.patientId,
        choice: payload.choice,
        enrollmentEmailSent: payload.enrollmentEmailSent,
        choiceMade: payload.choiceMade ? payload.choiceMade : '',
        appointment: payload.appointment ? payload.appointment : '',
        completedAppointmentOn: payload.completedAppointmentOn ? payload.completedAppointmentOn : '',
        completedSelfOn: payload.completedSelfOn ? payload.completedSelfOn : '',
      },
      lastUpdated,
      applicationStatus: APPLICATION_STATUS.OPEN,
    };
    await setDoc(docRef, data, { merge: true });
  }

  // HANDLE DisplayName UPDATES
  function isDisplayNameUpdate(
    payload:
      | ContactEmailUpdate
      | EnrollmentInfo
      | NavTypeUpdate
      | NavInfoUpdate
      | NavObjectUpdate
      | OnboardingUpdate
      | DisplayNameUpdate
      | FinalUpdate
      | TextPrefs,
  ): payload is DisplayNameUpdate {
    return (payload as DisplayNameUpdate).displayName !== undefined;
  }
  if (isDisplayNameUpdate(payload) && payload.displayName) {
    data = {
      displayName: payload.displayName,
      lastUpdated,
      applicationStatus: APPLICATION_STATUS.OPEN,
    };
    await setDoc(docRef, data, { merge: true });
  }
  // HANDLE NavTypeUPDATES (NO lastUpdated - NAV CHANGES ARE INTERNAL)
  function isNavType(
    payload:
      | ContactEmailUpdate
      | EnrollmentInfo
      | NavTypeUpdate
      | NavInfoUpdate
      | NavObjectUpdate
      | OnboardingUpdate
      | DisplayNameUpdate
      | FinalUpdate
      | TextPrefs,
  ): payload is NavTypeUpdate {
    return (
      (payload as NavTypeUpdate).navType !== undefined &&
      // MAKE SURE IT'S A NavTypeUpdate AND NOT A NavInfoUpdate
      //@ts-expect-error WE ARE FORCING THE CHECK HERE
      (payload as NavTypeUpdate).navObj === undefined &&
      //@ts-expect-error WE ARE FORCING THE CHECK HERE
      (payload as NavTypeUpdate).navInfo === undefined
    );
  }
  if (isNavType(payload) && payload.navType) {
    const data = {} as {
      navType: NavType;
    };
    data.navType = payload.navType;
    if (payload.navType !== undefined) {
      data.navType = payload.navType;
    }
    await setDoc(docRef, data, { merge: true });
    return payload.navType;
  }

  // HANDLE NavInfo UPDATES (NO lastUpdated - NAV CHANGES ARE INTERNAL)
  function isNavInfo(
    payload:
      | ContactEmailUpdate
      | EnrollmentInfo
      | NavTypeUpdate
      | NavInfoUpdate
      | NavObjectUpdate
      | OnboardingUpdate
      | DisplayNameUpdate
      | FinalUpdate
      | TextPrefs,
  ): payload is NavInfoUpdate {
    return (payload as NavInfoUpdate).navInfo !== undefined;
  }
  if (isNavInfo(payload) && payload.navInfo) {
    const data = {} as {
      navInfo: NavInfo;
      navType?: NavType;
      canText?: boolean;
    };
    data.navInfo = payload.navInfo;
    if (payload.navType !== undefined) {
      data.navType = payload.navType;
    }
    if (payload.canText !== undefined) {
      data.canText = payload.canText;
    }
    await setDoc(docRef, data, { merge: true });
    return payload.navInfo;
  }

  // HANDLE ONBOARDING UPDATES
  function isOnboardingUpdate(
    payload:
      | ContactEmailUpdate
      | EnrollmentInfo
      | NavTypeUpdate
      | NavInfoUpdate
      | NavObjectUpdate
      | OnboardingUpdate
      | DisplayNameUpdate
      | FinalUpdate
      | TextPrefs,
  ): payload is OnboardingUpdate {
    return (payload as OnboardingUpdate).completedOnboarding !== undefined;
  }

  if (isOnboardingUpdate(payload) && payload.completedOnboarding) {
    data = {
      completedOnboarding: payload.completedOnboarding,
      lastUpdated,
      applicationStatus: APPLICATION_STATUS.OPEN,
    };
    await setDoc(docRef, data, { merge: true });
    return payload.completedOnboarding;
  }

  // HANDLE NAV OBJ UPDATES (NO lastUpdated - NAV CHANGES ARE INTERNAL)
  function isNavObjUpdate(
    payload:
      | ContactEmailUpdate
      | EnrollmentInfo
      | NavTypeUpdate
      | NavInfoUpdate
      | NavObjectUpdate
      | OnboardingUpdate
      | DisplayNameUpdate
      | FinalUpdate
      | TextPrefs,
  ): payload is NavObjectUpdate {
    return (payload as NavObjectUpdate).navObj !== undefined;
  }

  if (isNavObjUpdate(payload) && payload) {
    data = {
      navObj: payload.navObj,
    };
    // CHECK IS NECESSARY TO DISAMBIGUATE BETWEEN CONTACT INFO UPDATE AND NAV OBJECT UPDATE
    if (data.navObj) {
      await setDoc(docRef, data, { merge: true });
    }
    return payload;
  }

  // HANDLE FilterOptions UPDATES
  function isFinalUpdate(
    payload:
      | ContactEmailUpdate
      | EnrollmentInfo
      | NavTypeUpdate
      | NavInfoUpdate
      | NavObjectUpdate
      | OnboardingUpdate
      | DisplayNameUpdate
      | FinalUpdate
      | TextPrefs,
  ): payload is FinalUpdate {
    return (payload as FinalUpdate).data?.applicationStatus !== undefined;
  }
  if (isFinalUpdate(payload) && payload.data) {
    data = { ...payload.data, lastUpdated };
    await setDoc(docRef, data, { merge: true });
    return payload;
  }
  // HANDLE TextPrefs UPDATES
  function isTextPrefsUpdate(
    payload:
      | ContactEmailUpdate
      | EnrollmentInfo
      | NavTypeUpdate
      | NavInfoUpdate
      | NavObjectUpdate
      | OnboardingUpdate
      | DisplayNameUpdate
      | FinalUpdate
      | TextPrefs,
  ): payload is TextPrefs {
    // TODO: INVESTIGATE SENTRY ERROR HERE: https://mideo.sentry.io/issues/6531833798/?alert_rule_id=15702719&alert_timestamp=1744211609231&alert_type=email&environment=production&notification_uuid=e33bafba-b04d-4753-ba06-bc7088af1d27&project=4508558575992832&referrer=alert_email
    // CAN BE FIXED BY ADDING A CHECK FOR payload.data OR OPTIONAL CHAINING

    return (payload as TextPrefs).data?.canText !== undefined;
  }
  if (isTextPrefsUpdate(payload) && payload.data) {
    await setDoc(docRef, { ...payload.data, lastUpdated, applicationStatus: APPLICATION_STATUS.OPEN }, { merge: true });
    return payload;
  }
};

export const useUpdateUserDoc = () => {
  return useMutation({
    mutationFn: (
      payload:
        | ContactEmailUpdate
        | EnrollmentInfo
        | NavTypeUpdate
        | NavInfoUpdate
        | NavObjectUpdate
        | OnboardingUpdate
        | DisplayNameUpdate
        | FinalUpdate
        | TextPrefs,
    ) => updateUserDoc(payload),
  });
};
